import { User } from '../../../models/User'
import { ActionSuggestion } from '../model'
import { canCreateOrUpdateContact, IntegrationServiceId } from '../../../common/integrations'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectRoomSession } from '../../../store/meetingSummary/roomSession'
import { useAccount } from '../../../common/useAccount'
import { useGetMeQuery } from '../../../store/user/user'
import { useMeetingUsers } from '../../../apps/professional-app/fa/MeetingSummary/hooks/users'
import { useFilterSuggestions } from './useFilterSuggestions'

const getUpdateContactSuggestionsForUser = (user: User, currentUser: User | undefined, currentUserId: string): ActionSuggestion[] => {
  const { id: userId, type } = user
  const userType = currentUserId === userId ? 'current' : (type ?? 'anonymous')
  const isAnonymous = userType === 'anonymous'
  const isExternal = userType === 'external'

  if (!isAnonymous && !isExternal) {
    return []
  }

  if (!currentUser?.integrations || currentUser.integrations.length === 0) {
    return []
  }

  const actions: ActionSuggestion[] = currentUser.integrations
    .filter((e) => canCreateOrUpdateContact(e.serviceId as IntegrationServiceId, user.integrations))
    .map(({ serviceId }): ActionSuggestion => {
      return {
        actionType: 'updateContact',
        serviceId: serviceId as IntegrationServiceId,
        user: user,
      }
    })

  return actions
}

export const useUpdateContactSuggestions = () => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const roomSession = useAppSelector(selectRoomSession)
  const { accountId } = useAccount({ refetchOnMountOrArgChange: true })
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { usersByRoles } = useMeetingUsers(roomSession)
  const allUsers: User[] = Object.values(usersByRoles).flat()

  const suggestions = allUsers.flatMap((user) => getUpdateContactSuggestionsForUser(user, currentUser, currentUserId))

  return useFilterSuggestions(suggestions, 'updateContact')
}
